import { useEffect, useRef, useState } from 'react';

export default function CircleExpand() {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    let animationFrameId;
    
    const handleScroll = () => {
      if (!sectionRef.current) return;

      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Check if section is in viewport
      const sectionTop = rect.top;
      const sectionBottom = rect.bottom;
      const isVisible = sectionTop < windowHeight && sectionBottom > 0;
      setIsInView(isVisible);

      if (!isVisible) return;

      // Calculate scroll progress within the section
      const sectionHeight = rect.height;
      const scrollTop = -rect.top;
      const scrollRange = sectionHeight - windowHeight;

      let progress = 0;
      if (scrollTop < 0) {
        progress = 0;
      } else if (scrollTop > scrollRange) {
        progress = 1;
      } else {
        progress = scrollTop / scrollRange;
      }

      // Smooth the progress value to prevent jumping
      setScrollProgress(prev => {
        const diff = progress - prev;
        // Add damping to smooth rapid changes
        return prev + diff * 0.3;
      });
    };

    const throttledScroll = () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      animationFrameId = requestAnimationFrame(handleScroll);
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });
    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', throttledScroll);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  // Circle scale grows from 0.1 to 6
  const circleScale = 0.1 + scrollProgress * 6;
  
  // Circle blur: starts very blurry (20px), becomes clear gradually
  const circleBlur = Math.max(0, 20 - scrollProgress * 40);
  
  // Text appears between 15% and 60% scroll progress with smooth transitions
  const textFadeInStart = 0.15;
  const textFadeInEnd = 0.30;
  const textFadeOutStart = 0.50;
  const textFadeOutEnd = 0.65;
  
  let textOpacity = 0;
  if (scrollProgress >= textFadeInStart && scrollProgress <= textFadeInEnd) {
    // Fade in
    textOpacity = (scrollProgress - textFadeInStart) / (textFadeInEnd - textFadeInStart);
  } else if (scrollProgress > textFadeInEnd && scrollProgress <= textFadeOutStart) {
    // Fully visible
    textOpacity = 1;
  } else if (scrollProgress > textFadeOutStart && scrollProgress <= textFadeOutEnd) {
    // Fade out
    textOpacity = 1 - ((scrollProgress - textFadeOutStart) / (textFadeOutEnd - textFadeOutStart));
  }
  
  // Font scaling effect - more gradual
  const textScale = 0.8 + textOpacity * 0.4;
  
  // Arrow/image appears after 65% scroll progress with smooth fade in
  const arrowStart = 0.65;
  const arrowEnd = 0.85;
  let arrowOpacity = 0;
  if (scrollProgress >= arrowStart && scrollProgress <= arrowEnd) {
    arrowOpacity = (scrollProgress - arrowStart) / (arrowEnd - arrowStart);
  } else if (scrollProgress > arrowEnd) {
    arrowOpacity = 1;
  }

  // Only render animations when section is in view
  if (!isInView) {
    return (
      <section ref={sectionRef} className="relative h-[300vh] bg-slate-900">
        <div className="sticky top-0 h-screen flex items-center justify-center">
          {/* Minimal static content when not in view */}
          <div className="text-white text-center">
            <p>Scroll to see animation</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="relative h-[300vh] bg-slate-900">
      <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Circle Background */}
        <div
          className="absolute rounded-full bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 transition-all duration-500 ease-out"
          style={{
            width: '100px',
            height: '100px',
            transform: `scale(${circleScale})`,
            filter: `blur(${circleBlur}px)`,
            opacity: Math.min(1, scrollProgress * 1.2),
          }}
        />

        {/* Text Content - Smooth fade in and out */}
        <div
          className="relative z-10 text-center px-4 transition-all duration-1000 ease-out"
          style={{ 
            opacity: textOpacity,
            transform: `scale(${textScale}) translateY(${(1 - textOpacity) * 20}px)`,
            pointerEvents: textOpacity > 0 ? 'auto' : 'none',
          }}
        >
          <h2 
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight"
            style={{
              textShadow: `0 0 ${textOpacity * 30}px rgba(16, 185, 129, 0.7)`,
              filter: `blur(${Math.max(0, 3 - textOpacity * 6)}px)`
            }}
          >
            Your Success
          </h2>
          <h3 
            className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight"
            style={{
              textShadow: `0 0 ${textOpacity * 25}px rgba(45, 212, 191, 0.7)`,
              filter: `blur(${Math.max(0, 3 - textOpacity * 6)}px)`
            }}
          >
            Is Our Mission
          </h3>
          <p 
            className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed transition-opacity duration-700"
            style={{
              opacity: textOpacity * 0.9,
            }}
          >
            We're committed to helping you achieve your commercial real estate goals
            with transparent, reliable, and innovative financing solutions
          </p>
        </div>

        {/* Arrow Image - Smooth appearance */}
        <div
          className="absolute z-10 transition-all duration-1000 ease-out flex flex-col items-center"
          style={{ 
            opacity: arrowOpacity,
            transform: `translateY(${arrowOpacity * 10}px)`,
            pointerEvents: arrowOpacity > 0 ? 'auto' : 'none',
          }}
        >
          <div 
            className="relative mb-4"
            style={{
              width: `${Math.min(circleScale * 80, 320)}px`,
              height: `${Math.min(circleScale * 80, 320)}px`,
              marginBottom: '10%',
              animation: arrowOpacity > 0.5 ? 'bounce 2s infinite' : 'none'
            }}
          >
            <img 
              src="/static/images/arrow.png" 
              alt="Let's Go"
              className="w-full h-full object-contain transition-transform duration-300"
              style={{
                filter: `drop-shadow(0 0 ${arrowOpacity * 20}px rgba(16, 185, 129, 0.5))`,
                transform: `scale(${0.9 + arrowOpacity * 0.2})`
              }}
            />
          </div>
          
          <p 
            className="text-white text-lg md:text-xl font-semibold text-center transition-all duration-500"
            style={{
              textShadow: `0 0 ${arrowOpacity * 15}px rgba(16, 185, 129, 0.7)`,
              opacity: arrowOpacity,
              transform: `translateY(${arrowOpacity * 5}px)`
            }}
          >
            Let's Go
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 transition-opacity duration-500"
          style={{ opacity: isInView ? 1 : 0 }}
        >
          <div className="flex items-center space-x-2 text-white/70">
            <div className="w-32 h-1 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${scrollProgress * 100}%` }}
              />
            </div>
            <span className="text-sm font-medium min-w-[40px]">
              {Math.round(scrollProgress * 100)}%
            </span>
          </div>
        </div>
      </div>

      {/* Add custom bounce animation */}
      <style jsx>{`
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </section>
  );
}